import { useState } from "react";
import { toast } from "sonner";
import { Handshake } from "lucide-react";
import { api, imgUrl, formatPrice, errText } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

export const OfferModal = ({ item, onSent, trigger }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const quick = [10, 15, 20].map((p) => ({ percent: p, price: Math.round(item.price * (100 - p)) / 100 }));
  const customVal = parseFloat(String(custom).replace(",", "."));
  const offerPrice = selected !== null ? quick[selected].price : (isNaN(customVal) ? null : Math.round(customVal * 100) / 100);
  const savings = offerPrice ? Math.round((item.price - offerPrice) * 100) / 100 : null;
  const savingsPct = offerPrice ? Math.round(((item.price - offerPrice) / item.price) * 100) : null;
  const valid = offerPrice && offerPrice > 0 && offerPrice < item.price;

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/offers", { item_id: item.id, offer_price: offerPrice });
      toast.success("Angebot gesendet! Der Verkäufer wurde benachrichtigt.");
      setOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="negotiate-btn"
            className="w-full bg-white border-2 border-foreground text-foreground font-bold py-4 rounded-full hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Handshake className="w-4 h-4" aria-hidden="true" /> Verhandeln
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-t-2xl sm:rounded-2xl">
        <DialogHeader><DialogTitle className="text-xl font-bold">Preis verhandeln</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3">
          {item.image_ids?.[0] && <img src={imgUrl(item.image_ids[0], "thumb")} alt="" className="w-14 h-14 rounded-xl object-cover" />}
          <div>
            <p className="text-sm font-semibold line-clamp-1">{item.title}</p>
            <p className="text-xs text-muted-foreground">Artikelpreis: <span className="font-bold text-foreground">{formatPrice(item.price)}</span></p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="quick-offers">
          {quick.map((q, i) => (
            <button key={q.percent} data-testid={`quick-offer-${q.percent}`}
              onClick={() => { setSelected(i); setCustom(""); }}
              className={`rounded-xl border-2 p-3 text-center transition-colors ${selected === i ? "border-accent bg-accent/5" : "border-border hover:border-foreground/40"}`}>
              <p className="text-sm font-bold">{formatPrice(q.price)}</p>
              <p className="text-[11px] text-muted-foreground">{q.percent} % weniger</p>
            </button>
          ))}
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Eigenen Preis eingeben</label>
          <input data-testid="custom-offer-input" type="number" inputMode="decimal" step="0.01" min="0.5" max={item.price}
            placeholder="z. B. 75,00"
            value={custom}
            onChange={(e) => { setCustom(e.target.value); setSelected(null); }}
            className="w-full rounded-xl border border-border px-4 py-3.5 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        {valid && (
          <div className="bg-secondary rounded-xl p-3 text-sm" data-testid="offer-savings">
            <span className="font-bold text-accent">Du sparst {formatPrice(savings)}</span>
            <span className="text-muted-foreground"> · {savingsPct} % unter dem Angebotspreis</span>
          </div>
        )}
        <button data-testid="send-offer-btn" onClick={send} disabled={!valid || busy}
          className="w-full bg-foreground text-white font-bold py-4 rounded-full hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-40 sticky bottom-0">
          {busy ? "Wird gesendet…" : "Angebot senden"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
